import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";

export default function About() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="About Us | Woods Spine & Wellness | Longview TX"
        description="Learn more about Woods Spine and Wellness, our vision, philosophy, and meet Dr. Tucker Woods and Dr. Lance Potter in Longview, TX."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/1fb1eb86-cc7a-4260-b2e8-4e398b3f3235.jpg"
        canonical="https://woodsspine.com/about"
      />
      <Navbar />
      <main id="main-content" className="flex-grow pt-24 pb-16">
        {/* Header Section */}
        <section className="container mx-auto px-4 mb-20 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6">
            Woods Spine and Wellness: <br />
            <span className="text-accent">Providing the Highest Quality of Care in Longview, TX</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Learn more About Woods Spine and Wellness
          </p>
        </section>

        {/* Vision, Philosophy, Mission */}
        <section className="container mx-auto px-4 mb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="aspect-square rounded-full overflow-hidden shadow-2xl max-w-md mx-auto lg:mx-0">
                <img 
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/1fb1eb86-cc7a-4260-b2e8-4e398b3f3235.jpg&w=1280&output=webp&q=80" 
                  alt="Spine model" 
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-12">
              <div>
                <h2 className="text-3xl font-bold text-primary mb-4">Our <span className="text-accent">Vision</span></h2>
                <p className="text-muted-foreground leading-relaxed">
                  At Woods Spine and Wellness, our vision is to be the leading provider of spine and wellness services in our community. We strive to be a beacon of health, healing, and vitality, and to inspire our patients to live their lives to the fullest. We envision a world where every individual has access to the highest quality care, and where everyone can experience the benefits of a healthy and active lifestyle. Through our commitment to excellence, innovation, and patient-centered care, we aim to make a positive impact on the health and well-being of our patients and our community.
                </p>
              </div>
              
              <div>
                <h2 className="text-3xl font-bold text-primary mb-4">Our <span className="text-accent">Philosophy</span></h2>
                <p className="text-muted-foreground leading-relaxed">
                  Woods Spine and Wellness focuses on prevention and recovery, alongside general wellness. Through advanced therapies, stretching, strengthening, and balancing, our goal is to ensure you are staying healthy and active while achieving your wellness & fitness goals.
                </p>
              </div>

              <div>
                <h2 className="text-3xl font-bold text-primary mb-4">Our <span className="text-accent">Mission</span></h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our mission at Woods Spine and Wellness is to promote optimal health and wellness for our patients through the highest quality care, functional movement assessments, and evidence-based wellness practices. We are dedicated to empowering individuals to take an active role in their health and well-being, and to provide a safe and welcoming space where our patients can heal, rejuvenate, and thrive. Our goal is to improve the quality of life for our patients by helping them achieve their health goals and live their best lives.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="bg-secondary/30 py-24">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-primary">Meet <span className="text-accent">the Team</span></h2>
            </div>

            <div className="space-y-12 max-w-5xl mx-auto">
              {/* Dr. Tucker Woods */}
              <Card id="dr-tucker-woods" className="overflow-hidden border-0 shadow-xl">
                <div className="grid md:grid-cols-3 gap-0">
                  <div className="md:col-span-1 h-full min-h-[400px]">
                    <img 
                      src="https://wsrv.nl/?url=https://assets.cdn.filesafe.space/7UxsnzcuJDZvyZp0wk8z/media/69c6d2042e89d8804a561325.jpeg&w=1280&output=webp&q=80" 
                      alt="Dr. Tucker Woods" 
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="md:col-span-2 p-8 md:p-12 flex flex-col justify-center bg-card">
                    <h3 className="text-3xl font-bold text-primary mb-2">Dr. Tucker Woods, DC</h3>
                    <p className="text-accent font-semibold mb-6">Spine & Wellness Specialist</p>
                    <div className="space-y-4 text-muted-foreground">
                      <p>Meet Dr. Tucker Woods, a highly skilled and experienced specialist in Longview, TX. Dr. Woods is a true East Texas native who has been providing exceptional care to the community since 2008.</p>
                      <p>After completing his undergraduate studies at East Texas Baptist University in 2003, Dr. Woods received his doctoral degree from Parker College in 2007. With over a decade of experience in the field, Dr. Woods has become a well-respected figure among his peers and patients alike.</p>
                      <p>In addition to his extensive clinical experience, Dr. Woods has also been actively involved in various organizations throughout his career. He has served as a past State Director for District 6 of the Texas Chiropractic Association, as well as Lions Club President and other various organizations.</p>
                      <p>Dr. Woods is also a sought-after speaker, presenting nationally to fellow practitioners for Pinnacle group. He specializes in hand and foot pain and spinal ligament injuries, which are often caused by automobile accidents. It's no surprise that his office has been voted a Locals Love Us award winner every year for Longview since 2012!</p>
                      <p>When he's not busy helping his patients achieve optimal health and wellness, Dr. Woods enjoys exercising, playing, and watching sports, including the Texas Rangers and Dallas Cowboys. But above all, he is a man of faith, father of 3 daughters, Blair, Bella, and Brynn. He is dedicated to the health and well-being of families in the Marshall/Hallsville/Longview area.</p>
                      <p>If you're looking for a reliable and compassionate specialist in Longview, TX, look no further than Dr. Tucker Woods. With his expertise and commitment to patient care, you can rest assured that you're in good hands.</p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Dr. Lance Potter */}
              <Card id="dr-lance-potter" className="overflow-hidden border-0 shadow-xl">
                <div className="grid md:grid-cols-3 gap-0">
                  <div className="md:col-span-1 h-full min-h-[400px]">
                    <img 
                      src="https://wsrv.nl/?url=https://assets.cdn.filesafe.space/7UxsnzcuJDZvyZp0wk8z/media/6615a6bdb198161b5eb9dc63.jpeg&w=1280&output=webp&q=80" 
                      alt="Dr. Lance Potter" 
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="md:col-span-2 p-8 md:p-12 flex flex-col justify-center bg-card">
                    <h3 className="text-3xl font-bold text-primary mb-2">Dr. Lance Potter, D.C.</h3>
                    <p className="text-accent font-semibold mb-6">Spine & Wellness Specialist</p>
                    <div className="space-y-4 text-muted-foreground">
                      <p>Dr. Lance Potter obtained his Doctorate in 2009 from Parker University in Dallas. He also has a Bachelor's of Science in Nursing from the University of Texas at Arlington, as well as a Bachelor's of General Studies from Texas Tech University, which includes Biology, Chemistry, and English minors.</p>
                      <p>He has practiced in various wellness clinics and hospitals around East Texas since graduating in 2009. His clinical experience includes family practice, sports medicine, and working in a stem cell and joint regeneration clinic. He has worked in emergency medicine and surgical services as a Registered Nurse.</p>
                      <p>Dr. Potter and his spouse Jennifer have been married for 17 years and have a 10-year-old son (Carson), and a 6-year-old daughter (Avery). Jennifer attended Texas Tech University Health Sciences school of pharmacy to receive her Doctor of Pharmacy degree and she currently practices for a family-owned pharmacy.</p>
                      <p>Dr. Potter is native to East Texas. He grew up in Gilmer and Mount Pleasant. He and his family are members of Grace Creek Church. He enjoys his weekly bowling league, watching his son play soccer, listening to his daughter play piano, and grilling by the pool in his spare time.</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
